if (!customElements.get('ovrze-decision-product')) {
  customElements.define('ovrze-decision-product', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      const events = { signal: this.controller.signal };
      this.variants = JSON.parse(this.querySelector('[data-product-variants]').textContent);
      this.strings = {
        add: this.dataset.addText,
        sold_out: this.dataset.soldOutText,
        unavailable: this.dataset.unavailableText
      };
      this.select = this.querySelector('[data-variant-select]');
      this.form = this.querySelector('form');
      this.fieldsets = [...this.querySelectorAll('[data-option-index]')];
      const initialVariant = this.variants.find(variant => String(variant.id) === this.select.value);
      this.fieldsets.forEach((fieldset, index) => fieldset.querySelectorAll('input').forEach(input => {
        input.checked = input.value === initialVariant?.options[index];
      }));
      this.media = [...this.querySelectorAll('[data-media-id]')];
      this.classList.add('is-enhanced');
      const initialMedia = this.querySelector('[data-current-media]') || this.media[0];
      if (initialMedia) this.showMedia(initialMedia.dataset.mediaId);
      this.querySelectorAll('[data-media-link]').forEach(link => {
        link.addEventListener('click', event => {
          event.preventDefault();
          const media = this.media.find(item => item.dataset.mediaId === link.dataset.mediaLink);
          const color = media?.dataset.mediaColor;
          const colorField = this.fieldsets.find(field => /farbe|color|colour/.test(field.dataset.optionName));
          if (color && colorField) {
            const input = [...colorField.querySelectorAll('input')].find(item => item.value === color);
            if (input) { input.checked = true; this.updateVariant(Number(colorField.dataset.optionIndex), color); }
          }
          this.showMedia(link.dataset.mediaLink);
        }, events);
      });
      const picker = this.querySelector('[data-option-picker]');
      if (picker) {
        picker.hidden = false;
        this.querySelector('[data-native-picker]').hidden = true;
        this.fieldsets.forEach(fieldset => fieldset.addEventListener('change', event => {
          this.updateVariant(Number(fieldset.dataset.optionIndex), event.target.value);
        }, events));
      }
      this.select.addEventListener('change', () => this.applyVariant(this.variants.find(variant => String(variant.id) === this.select.value)), events);
      this.form.addEventListener('submit', event => {
        const variant = this.variants.find(item => String(item.id) === this.select.value);
        if (!variant || !variant.available) event.preventDefault();
      }, events);
      this.updateVariant(null, null, false);
      this.setupSizeGuide(events);
      this.querySelectorAll('[data-copy-coupon]').forEach(button => {
        button.addEventListener('click', async () => {
          const status = button.closest('[data-coupon]').querySelector('[data-copy-status]');
          let copied = false;
          try {
            if (navigator.clipboard?.writeText && window.isSecureContext) {
              await navigator.clipboard.writeText(button.dataset.code);
              copied = true;
            } else {
              const input = document.createElement('textarea');
              input.value = button.dataset.code;
              input.setAttribute('readonly', '');
              input.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0;';
              document.body.append(input);
              input.select();
              copied = document.execCommand('copy');
              input.remove();
              button.focus();
            }
          } catch (_) {
            copied = false;
          }
          status.textContent = copied ? button.dataset.success : button.dataset.error;
          button.classList.toggle('is-copied', copied);
          if (!copied) {
            const code = button.closest('[data-coupon]').querySelector('[data-coupon-code]');
            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(code);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }, events);
      });
      const sticky = this.querySelector('[data-sticky-purchase]');
      const primaryButton = this.form.querySelector('[data-add-button]');
      if (sticky && primaryButton && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          const rect = entries[0].boundingClientRect;
          sticky.hidden = entries[0].isIntersecting || rect.top > window.innerHeight;
        }, { threshold: 0 });
        this.observer.observe(primaryButton);
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.initialized = false;
    }
    showMedia(id) {
      this.media.forEach(media => {
        const active = media.dataset.mediaId === String(id);
        media.hidden = !active;
        if (!active) media.querySelectorAll('video').forEach(video => video.pause());
      });
      this.querySelectorAll('[data-media-link]').forEach(link => {
        if (link.dataset.mediaLink === String(id)) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }
    updateVariant(changedIndex, changedValue, updateMedia = true) {
      if (!this.fieldsets.length) {
        this.applyVariant(this.variants.find(variant => String(variant.id) === this.select.value), updateMedia);
        return;
      }
      let values = this.fieldsets.map(fieldset => fieldset.querySelector('input:checked')?.value);
      let variant = this.variants.find(item => item.options.every((value, index) => value === values[index]));
      if (!variant && changedIndex !== null) {
        variant = this.variants.find(item => item.options[changedIndex] === changedValue && item.options.every((value, index) => index >= changedIndex || value === values[index]));
        variant ||= this.variants.find(item => item.options[changedIndex] === changedValue && item.available);
        variant ||= this.variants.find(item => item.options[changedIndex] === changedValue);
      }
      if (variant) {
        values = variant.options;
        this.fieldsets.forEach((fieldset, index) => {
          fieldset.querySelectorAll('input').forEach(input => { input.checked = input.value === values[index]; });
          fieldset.querySelector('[data-selected-option]').textContent = values[index];
        });
      }
      this.fieldsets.forEach((fieldset, index) => {
        fieldset.querySelectorAll('input').forEach(input => {
          const possible = this.variants.filter(item => item.options[index] === input.value && item.options.every((value, priorIndex) => priorIndex >= index || value === values[priorIndex]));
          input.disabled = !possible.length;
          input.closest('.ozd-option-value').classList.toggle('is-sold-out', possible.length > 0 && !possible.some(item => item.available));
        });
      });
      this.applyVariant(variant, updateMedia);
    }
    applyVariant(variant, updateMedia = true) {
      const available = !!variant?.available;
      const label = !variant ? this.strings.unavailable : available ? this.strings.add : this.strings.sold_out;
      this.querySelectorAll('[data-add-button]').forEach(button => { button.disabled = !available; });
      this.querySelectorAll('[data-add-label]').forEach(element => { element.textContent = label; });
      const status = this.querySelector('[data-variant-status]');
      if (status) status.textContent = available ? '' : label;
      if (!variant) {
        this.select.value = '';
        return;
      }
      this.select.value = String(variant.id);
      const money = new Intl.NumberFormat(this.dataset.locale || 'de-DE', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(variant.price / 100);
      this.querySelectorAll('[data-product-price]').forEach(element => { element.textContent = money; });
      const compare = Number(variant.compare_at_price || 0);
      const onSale = compare > variant.price;
      const formatMoney = value => new Intl.NumberFormat(this.dataset.locale || 'de-DE', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(value / 100);
      this.querySelectorAll('[data-compare-price]').forEach(element => { element.hidden = !onSale; });
      this.querySelectorAll('[data-compare-price-value]').forEach(element => { element.textContent = onSale ? formatMoney(compare) : ''; });
      this.querySelectorAll('[data-sale-badge]').forEach(element => {
        element.hidden = !onSale;
        element.textContent = onSale ? `−${Math.floor((compare - variant.price) * 100 / compare)} %` : '';
      });
      this.querySelectorAll('[data-sale-saving]').forEach(element => {
        element.textContent = onSale ? element.dataset.savingTemplate.replace('__AMOUNT__', formatMoney(compare - variant.price)) : '';
      });
      if (updateMedia && variant.media_id !== this.lastVariantMedia && variant.media_id && this.media.some(media => media.dataset.mediaId === String(variant.media_id))) this.showMedia(variant.media_id);
      this.lastVariantMedia = variant.media_id;
      if (updateMedia) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }
    setupSizeGuide(events) {
      const dialog = this.querySelector('.ozd-size-dialog');
      const opener = this.querySelector('[data-size-open]');
      const fallback = this.querySelector('[data-size-fallback]');
      if (!dialog || !opener || typeof dialog.showModal !== 'function') return;
      const source = this.querySelector('[data-product-size-source]');
      const tables = source?.content.querySelectorAll('table');
      if (!tables?.length) return;
      const destination = this.querySelector('[data-size-tables]');
      tables.forEach(table => {
        const clone = table.cloneNode(true);
        clone.removeAttribute('id');
        clone.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
        destination.append(clone);
      });
      opener.hidden = false;
      fallback.hidden = true;
      opener.addEventListener('click', () => dialog.showModal(), events);
      this.querySelector('[data-size-close]').addEventListener('click', () => dialog.close(), events);
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      }, events);
      dialog.addEventListener('close', () => opener.focus(), events);
    }
  });
}

